// Run: full-screen ocean, instrument strip, warp, decision dialog, engine audio. The frame loop drives
// RunController.advance(); the DOM updates at ~10 Hz; the loop and the audio pause while the tab is hidden.
import type { AppContext, ScreenHandle, ScreenParams } from './app';
import type { Decision, Warp } from '../types';
import { WARP_LEVELS } from '../types';
import { RunController } from '../sim/run';
import { generateRoute, todaySeed } from '../sim/route';
import { computeBaseline } from '../sim/score';
import { recordRun } from '../save';
import { h, clear, button, photo, confirmDialog } from './dom';
import { fmtFuel } from './format';
import { craftBuild, type CraftBuild } from './build';
import { OceanMap } from './oceanMap';
import { makeInstruments, type Instruments } from './instruments';

const DOM_HZ = 10;
/** Delay before the decoded engine bank is released, so the stop one-shot can finish. */
const DISPOSE_DELAY_MS = 4000;

export function mountRun(root: HTMLElement, ctx: AppContext): ScreenHandle {
  let ctl: RunController | null = null;
  let build: CraftBuild | null = null;
  let map: OceanMap | null = null;
  let inst: Instruments | null = null;
  let raf = 0;
  let last = 0;
  let realS = 0;
  let acc = 0;
  let finished = false;
  /** True while the End-run confirm is open: the clock stops so the player is not burning fuel at warp while reading it. */
  let paused = false;
  let mapGen = 0;

  const dlg = document.getElementById('decision') as HTMLDialogElement | null;
  const mapHost = h('div.map-host', { 'aria-hidden': 'true' });
  const mapFallback = h('div.map-fallback', { hidden: true });
  const chipPhoto = h('div');
  const chipTitle = h('h1', { id: 'run-title' }, 'Run');
  const chipSub = h('p', {}, '');
  const warpGroup = h('div.seg', { role: 'group', 'aria-label': 'Time warp' });
  const endBtn = button('End run', () => void endRun(), { cls: 'btn btn-danger' });
  const audioNotice = h('p.audio-notice', { role: 'status', hidden: true });
  const mapNotice = h('p.audio-notice', { role: 'status', hidden: true }, 'Map unavailable on this device; the run continues.');
  const bottom = h('div.run-bottom');
  const hud = h('div.run-hud', {},
    h('div.run-top', {},
      h('div.chip', {}, chipPhoto, h('div', {}, chipTitle, chipSub)),
      h('div.run-controls', {}, warpGroup, endBtn),
    ),
    h('div.run-mid', {}, audioNotice, mapNotice),
    bottom,
  );
  root.append(mapHost, mapFallback, hud);

  const warpButtons = new Map<string, HTMLButtonElement>();
  for (const level of WARP_LEVELS) {
    const b = button(`${level}×`, () => setWarp(level), { cls: 'btn', ariaLabel: `Time warp ${level} times` });
    warpButtons.set(String(level), b);
    warpGroup.append(b);
  }
  const autoBtn = button('Auto', () => setWarp('auto'), { cls: 'btn', ariaLabel: 'Automatic time warp' });
  warpButtons.set('auto', autoBtn);
  warpGroup.append(autoBtn);

  function setWarp(level: Warp | 'auto'): void {
    if (!ctl) return;
    if (level === 'auto') ctl.autoWarp = true;
    else { ctl.autoWarp = false; ctl.warp = level; }
    paintWarp();
  }

  function paintWarp(): void {
    if (!ctl) return;
    for (const [key, b] of warpButtons) b.setAttribute('aria-pressed', String(ctl.autoWarp ? key === 'auto' : key === String(ctl.warp)));
  }

  function onVisibility(): void {
    if (document.hidden) {
      cancelAnimationFrame(raf);
      raf = 0;
      ctx.audio.voice?.setVolume(0);
    } else if (ctl && !finished && !raf) {
      ctx.audio.voice?.setVolume(ctx.save.settings.audio ? ctx.save.settings.volume : 0);
      last = performance.now();
      raf = requestAnimationFrame(frame);
    }
  }

  function frame(now: number): void {
    raf = requestAnimationFrame(frame);
    const dt = Math.min(0.1, Math.max(0, (now - last) / 1000));
    last = now;
    if (!ctl || !build || finished) return;
    if (!paused && !ctl.pendingDecision && !ctl.ended) ctl.advance(dt);
    const s = ctl.state;
    ctx.audio.voice?.set(s.rpm, s.load);
    realS += dt;
    map?.frame(realS, s.timeS, ctl.routePositionNm);
    acc += dt;
    if (acc >= 1 / DOM_HZ) {
      acc = 0;
      inst?.update(s, ctl.effectiveWarp(), ctl.autoWarp, capacity());
      chipSub.textContent = build.rc.air ? (s.phase === 'climb' ? 'Climbing' : 'Cruise') : 'Under way';
      paintWarp();
    }
    if (ctl.pendingDecision && dlg && !dlg.open) openDecision(ctl.pendingDecision);
    if (ctl.ended) finish();
  }

  function capacity(): number {
    return build?.rc.air ? build.rc.air.fuelCapacityKg : build?.rc.sea ? build.rc.sea.fuelCapacityKg : 0;
  }

  function openDecision(d: Decision): void {
    if (!dlg) return;
    clear(dlg);
    const options = h('div.dialog-options');
    for (const o of d.options) {
      const b = button('', () => choose(o.id), { cls: 'btn dialog-option', ariaLabel: `${o.label}: ${o.consequence}` });
      b.append(h('b', {}, o.label), h('small', {}, o.consequence));
      options.append(b);
    }
    dlg.append(h('h2.dialog-title', { id: 'decision-title' }, d.title), h('p.dialog-detail', {}, d.detail), options);
    dlg.addEventListener('cancel', (e) => e.preventDefault(), { once: true });
    dlg.showModal();
    options.querySelector<HTMLButtonElement>('button')?.focus();
  }

  function choose(id: string): void {
    dlg?.close();
    if (!ctl) return;
    ctl.choose(id);
    last = performance.now();
    paintWarp();
  }

  async function endRun(): Promise<void> {
    if (!ctl || !build || finished) return;
    const left = fmtFuel(ctl.state.fuelKg, build.craft.domain, build.big);
    paused = true;
    const ok = await confirmDialog('End the run now?', `You stop with ${left} of fuel left. The distance so far counts.`, 'End run');
    paused = false;
    if (!ok || !ctl || finished) return;
    ctl.endRun();
    finish();
  }

  function finish(): void {
    if (!ctl || finished) return;
    finished = true;
    cancelAnimationFrame(raf);
    raf = 0;
    const raw = ctl.result();
    if (!raw) return;
    const voice = ctx.audio.voice;
    if (voice) {
      voice.stop();
      ctx.audio.voice = null;
      window.setTimeout(() => voice.dispose(), DISPOSE_DELAY_MS);
    }
    const { save, result } = recordRun(ctx.save, raw);
    ctx.save = save;
    ctx.persist();
    ctx.go('results', { result });
  }

  async function mountMap(gen: number): Promise<void> {
    if (!ctl) return;
    try {
      const m = await OceanMap.create(mapHost, ctl.route, ctx.reducedMotion());
      if (gen !== mapGen || finished) { m.destroy(); return; }
      map = m;
      mapFallback.hidden = true;
      mapNotice.hidden = true;
    } catch {
      mapFallback.hidden = false;
      mapNotice.hidden = false;
    }
  }

  function show(params: ScreenParams): void {
    hide();
    finished = false;
    const id = ctx.session.craftId;
    build = id ? craftBuild(ctx.save, id) : null;
    const controls = ctx.session.controls;
    clear(bottom);
    if (!build || !controls) {
      chipTitle.textContent = 'No run planned';
      chipSub.textContent = 'Go back to the hangar and plan a route.';
      bottom.append(button('Hangar', () => ctx.go('hangar'), { cls: 'btn btn-primary' }));
      return;
    }
    const { craft, engine, mods } = build;
    const route = generateRoute(todaySeed(), craft.domain);
    ctl = new RunController({ craft, engine, mods, controls, route, baseline: computeBaseline(craft, engine) });
    clear(chipPhoto);
    chipPhoto.append(photo(`/img/craft/${craft.photo}.webp`, '', craft.modelledOn));
    chipTitle.textContent = craft.className;
    chipSub.textContent = build.rc.air ? 'Climbing' : 'Under way';
    inst = makeInstruments(build.rc, craft.domain, build.big);
    bottom.append(inst.root);
    inst.update(ctl.state, ctl.effectiveWarp(), ctl.autoWarp, capacity());
    const notice = params.audioNotice ?? ctx.session.audioNotice;
    audioNotice.hidden = !notice;
    audioNotice.textContent = notice ?? '';
    mapFallback.hidden = true;
    mapNotice.hidden = true;
    paintWarp();
    realS = 0; acc = 0;
    document.addEventListener('visibilitychange', onVisibility);
    ctx.audio.voice?.start();
    ctx.audio.voice?.setVolume(ctx.save.settings.audio ? ctx.save.settings.volume : 0);
    last = performance.now();
    raf = requestAnimationFrame(frame);
    void mountMap(++mapGen);
  }

  function hide(): void {
    cancelAnimationFrame(raf);
    raf = 0;
    document.removeEventListener('visibilitychange', onVisibility);
    if (dlg?.open) dlg.close();
    map?.destroy();
    map = null;
    mapGen++;
    if (ctl && !finished) {
      // Leaving mid-run without a result: the engine recording stops and the controller is dropped.
      const voice = ctx.audio.voice;
      if (voice) { voice.stop(); ctx.audio.voice = null; window.setTimeout(() => voice.dispose(), DISPOSE_DELAY_MS); }
    }
    ctl = null;
    inst = null;
  }

  return { show, hide };
}
